import { Client } from '@stomp/stompjs';
import { parseJwt } from "@/lib/jwt";

const callbacks = () => ({
    onConnect: [],
    onDisconnect: []
});

export default class ChatAdapter {
    constructor(app, brokerUrl, options) {
        this.app = app;
        this.brokerUrl = brokerUrl;
        this.callbacks = callbacks();
        this.userId = null;
        this.options = {
            DEBUG: false,
            MESSAGES_BATCH_SIZE: 100,
            MAX_CHANNELS: 5000,
            CHANNEL_LOOKBACK_DAYS: 45,
            RECONNECT_DELAY_MS: 5000,
            HEARTBEAT_INCOMING_MS: 4000,
            HEARTBEAT_OUTGOING_MS: 4000,
            ...options
        }
    }

    activate() {
        this.client.activate();
    }

    async deactivate() {
        this.callbacks = callbacks();
        this.client.deactivate();
        this._client = null;
    }

    get client() {
        // Lazy initialization
        if (!this._client) {
            const token = this.app.$auth.strategy.token.get();
            this.userId = parseJwt(token).sub;
            const connectHeaders = {
                Authorization: token,
                DeviceToken: 'Care Portal',
            };
            console.log(`Connecting to ${this.brokerUrl}`)
            this._client = new Client({
                brokerURL: this.brokerUrl,
                connectHeaders: connectHeaders,
                debug: (str) => {
                    if (this.options.DEBUG) console.log("ChatAdapter:", str);
                },
                reconnectDelay: this.options.RECONNECT_DELAY_MS,
                heartbeatIncoming: this.options.HEARTBEAT_INCOMING_MS,
                heartbeatOutgoing: this.options.HEARTBEAT_OUTGOING_MS,

                onWebSocketClose: () => {
                    console.log('ChatAdapter: WebSocket connection closed');
                    this.callbacks.onDisconnect.forEach(c => c())
                },

                onConnect: (frame) => {
                    console.log('ChatAdapter: Connected', frame)
                    this.callbacks.onConnect.forEach(c => c())
                },
                onDisconnect: (frame) => {
                    console.log('ChatAdapter: Disconnected', frame)
                    this.callbacks.onDisconnect.forEach(c => c())
                },
                onStompError: (frame) => {
                    const msg = frame.headers['message'] || 'Unknown error';
                    console.error('Broker reported error: ' + msg);
                    console.error('Additional details: ' + frame.body);
                    if (msg.includes('Unauthorized')) {
                        // Logout user
                        this.app.$auth.logout()
                    }
                }
            });
        }
        return this._client;
    }

    // Callbacks

    onConnect(callback) {
        this.callbacks.onConnect.push(callback);
    }

    onDisconnect(callback) {
        this.callbacks.onDisconnect.push(callback);
    }

    // Requests

    sendMessage(medium, profileId, message) {
        const payload = { profileId, message };
        switch (medium) {
            case 'Sms':
                return this.#sendRequest(`/app/v3/sendOutboundSmsMessage`, payload);
            case 'InApp':
                return this.#sendRequest(`/app/v3/sendOutboundAppMessage`, payload);
            case 'AI':
                return this.#sendRequest(`/app/v3/askAI`, { message });
        }
        throw new Error(`Unknown medium ${medium}`);
    }

    getChannels() {
        const payload = {
            paging: { page: 0, size: this.options.MAX_CHANNELS, sort: ["lastModifiedDate,desc"] }
        };
        return this.#sendRequest(`/app/v3/getChannels`, payload)
            .then(data => {
                const processedChannels = data.channels.map(this.#processChannel.bind(this));
                return processedChannels;
            });
    }

    getPrioritizedChannels() {
        const payload = {
            paging: { days: this.options.CHANNEL_LOOKBACK_DAYS, limit: this.options.MAX_CHANNELS }
        };
        return this.#sendRequest(`/app/v3/getPrioritizedChannels`, payload)
            .then(data => data.channels.map(this.#processChannel.bind(this)));
    }

    getMessages(channelId, createdBefore = undefined) {
        const payload = {
            channelId,
            createdBefore,
            paging: { page: 0, size: this.options.MESSAGES_BATCH_SIZE, sort: ["createdDate,desc"] },
        };
        return this.#sendRequest(`/app/v3/getMessages`, payload)
            .then(({ messages, totalCount }) => ({
                messages: messages.map(this.#processMessage.bind(this)),
                hasEarlierMessages: totalCount > this.options.MESSAGES_BATCH_SIZE
            }))
    }

    readMessage(messageId) {
        const payload = { messageId };
        return this.#sendRequest(`/app/v3/readMessage`, payload)
            .then(data => this.#processChannel(data.channel));
    }

    unreadMessage(messageId) {
        const payload = { messageId };
        return this.#sendRequest(`/app/v3/unreadMessage`, payload)
            .then(data => {
                return this.#processChannel(data.channel);
            })
            .catch(error => {
                console.error('ChatAdapter: unreadMessage() error:', error);
                throw error;
            });
    }

    watchChannel(channelId) {
        const payload = { channelId };
        return this.#sendRequest(`/app/v3/watchChannel`, payload)
            .then(data => this.#processChannel(data.channel));
    }

    unwatchChannel(channelId, message) {
        const payload = { channelId, message };
        return this.#sendRequest(`/app/v3/unwatchChannel`, payload)
            .then(data => this.#processChannel(data.channel));
    }

    inviteToChannel(channelId, userId, message) {
        const payload = { channelId, userId, message };
        return this.#sendRequest(`/app/v3/inviteToChannel`, payload)
            .then(data => this.#processChannel(data.channel));
    }

    inviteToChannelByEmail(channelId, email, message) {
        const payload = { channelId, email, message };
        return this.#sendRequest(`/app/v3/inviteToChannelByEmail`, payload)
            .then(data => this.#processChannel(data.channel));
    }

    // Subscriptions

    onChannelUpdated(callback) {
        return this.client.subscribe('/topic/channels', (frame) => {
            const { data: { channel } } = JSON.parse(frame.body);
            this.#log(`Incoming channel data=${frame.body}`);
            callback(this.#processChannel(channel));
        });
    }

    onChannelMessage(principal, onMessage, onMessageStatus, onMessageStatusByDate) {
        return this.client.subscribe(`/topic/${principal}`, (frame) => {
            const { type, data } = JSON.parse(frame.body);
            this.#log(`Incoming message data=${frame.body}`);
            switch (type) {
                case 'MESSAGE': return onMessage(this.#processMessage(data));
                case 'UPDATE_MESSAGE_STATUS': return onMessageStatus(data);
                case 'UPDATE_MESSAGE_STATUS_BY_DATE': return onMessageStatusByDate(data);
                default: console.log(`Unknown message type: ${type}: ${data}`);
            }
        });
    }

    // Internal

    #processChannel(channel) {
        channel.isAI = channel.patientFirstName === 'AI';
        channel.participantsMap = new Map(
            channel.participants.map(p => [p.principal, p])
        );

        channel.ownerParticipant = channel.participantsMap.get(channel.owner);
        channel.patientParticipant = channel.participantsMap.get(channel.principal);
        // Get your participant record (don't fall back to owner)
        channel.myParticipant = channel.participantsMap.get(this.userId);

        // Smart unread count logic:
        // 1. If you're a participant, show YOUR unread count
        // 2. If you're not a participant but you're the owner, show owner's unread count  
        // 3. If you're not involved at all, show 0
        if (channel.myParticipant) {
            // You're a participant - show your personal unread count
            channel.unreadMessages = channel.myParticipant.unreadMessagesCount || 0;
        } else if (channel.owner === this.userId) {
            // You're the owner but not a participant yet - show owner's unread count
            channel.unreadMessages = channel.ownerParticipant?.unreadMessagesCount || 0;
        } else {
            // You're not involved - show 0
            channel.unreadMessages = 0;
        }
        channel.lastRead = channel.readUntil;
        return channel
    }

    #processMessage(message) {
        message.isMyMessage = message.createdBy === this.userId;
        return message
    }

    #sendRequest(destination, payload = {}) {
        this.#log(`Sending request destination=${destination}, payload=${JSON.stringify(payload)}`);
        return new Promise((resolve, reject) => {
            const { unsubscribe } = this.client.subscribe(destination, (frame) => {
                const { error, data } = JSON.parse(frame.body);
                this.#log(`Response for destination=${destination}, error=${error}, data=${JSON.stringify(data)}`);
                // Unsubscribe once the response is received (not needed for rabbitmq)
                unsubscribe();
                if (error) {
                    reject(error);
                } else {
                    resolve(data);
                }
            }, { "payload": JSON.stringify(payload) });
        });
    }

    #log(message) {
        if (this.options.DEBUG) {
            console.log(`ChatAdapter: ${message}`);
        }
    }
}
